interface Transaction {
  id: string;
  type: "CREDIT" | "DEBIT";
  amountCents: number;
  createdAt: string;
  merchantName?: string;
  description?: string;
}

interface SpendingDataPoint {
  date: string;
  credits: number;
  debits: number;
  net: number;
}

interface CategoryData {
  category: string;
  amount: number;
  percentage: number;
  color: string;
}

// Prepare spending data for line/area charts
export function prepareSpendingData(
  transactions: Transaction[],
  days: number = 30
): SpendingDataPoint[] {
  const today = new Date();
  const dataMap = new Map<string, { credits: number; debits: number }>();

  // Initialize all dates with zero values
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    const dateStr = date.toISOString().split("T")[0];
    dataMap.set(dateStr, { credits: 0, debits: 0 });
  }

  // Aggregate transactions by date
  transactions.forEach((txn) => {
    const date = new Date(txn.createdAt).toISOString().split("T")[0];
    if (dataMap.has(date)) {
      const current = dataMap.get(date)!;
      if (txn.type === "CREDIT") {
        current.credits += txn.amountCents / 100;
      } else {
        current.debits += txn.amountCents / 100;
      }
    }
  });

  // Convert to array format
  return Array.from(dataMap.entries())
    .map(([date, values]) => ({
      date: formatChartDate(date),
      credits: Math.round(values.credits),
      debits: Math.round(values.debits),
      net: Math.round(values.credits - values.debits),
    }))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
}

// Format date for chart display (e.g., "Jan 15")
function formatChartDate(dateStr: string): string {
  const date = new Date(dateStr);
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const day = date.getDate();
  return `${month} ${day}`;
}

// Extract category from merchant name or description
function extractCategory(transaction: Transaction): string {
  const text = (transaction.merchantName || transaction.description || "").toLowerCase();

  if (text.includes("amazon") || text.includes("flipkart") || text.includes("shopping")) {
    return "Shopping";
  }
  if (text.includes("restaurant") || text.includes("food") || text.includes("zomato") || text.includes("swiggy")) {
    return "Food & Dining";
  }
  if (text.includes("uber") || text.includes("ola") || text.includes("travel") || text.includes("flight")) {
    return "Travel";
  }
  if (text.includes("netflix") || text.includes("prime") || text.includes("spotify") || text.includes("entertainment")) {
    return "Entertainment";
  }
  if (text.includes("electricity") || text.includes("water") || text.includes("bill") || text.includes("utility")) {
    return "Bills & Utilities";
  }
  if (text.includes("grocery") || text.includes("supermarket")) {
    return "Groceries";
  }
  if (text.includes("health") || text.includes("pharmacy") || text.includes("hospital")) {
    return "Healthcare";
  }

  return "Other";
}

// Prepare category spending data for pie/donut charts
export function prepareCategoryData(transactions: Transaction[]): CategoryData[] {
  const categoryMap = new Map<string, number>();

  // Only consider debit transactions for spending
  transactions
    .filter((txn) => txn.type === "DEBIT")
    .forEach((txn) => {
      const category = extractCategory(txn);
      categoryMap.set(category, (categoryMap.get(category) || 0) + txn.amountCents / 100);
    });

  const total = Array.from(categoryMap.values()).reduce((sum, val) => sum + val, 0);

  // Convert to array and add percentages
  const categories = Array.from(categoryMap.entries())
    .map(([category, amount]) => ({
      category,
      amount: Math.round(amount),
      percentage: total > 0 ? Math.round((amount / total) * 100) : 0,
      color: getCategoryColor(category),
    }))
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 6); // Top 6 categories

  return categories;
}

// Get consistent colors for categories
export function getCategoryColor(category: string): string {
  const colorMap: Record<string, string> = {
    Shopping: "hsl(var(--chart-1))",
    "Food & Dining": "hsl(var(--chart-2))",
    Travel: "hsl(var(--chart-3))",
    Entertainment: "hsl(var(--chart-4))",
    "Bills & Utilities": "hsl(var(--chart-5))",
    Groceries: "hsl(220, 70%, 50%)",
    Healthcare: "hsl(340, 75%, 55%)",
    Other: "hsl(var(--muted))",
  };

  return colorMap[category] || "hsl(var(--muted))";
}

// Calculate trends (month-over-month)
export function calculateTrends(
  currentPeriod: Transaction[],
  previousPeriod: Transaction[]
): {
  credits: { value: number; change: number; trend: "up" | "down" | "neutral" };
  debits: { value: number; change: number; trend: "up" | "down" | "neutral" };
  net: { value: number; change: number; trend: "up" | "down" | "neutral" };
} {
  const currentCredits =
    currentPeriod
      .filter((t) => t.type === "CREDIT")
      .reduce((sum, t) => sum + t.amountCents, 0) / 100;

  const currentDebits =
    currentPeriod
      .filter((t) => t.type === "DEBIT")
      .reduce((sum, t) => sum + t.amountCents, 0) / 100;

  const previousCredits =
    previousPeriod
      .filter((t) => t.type === "CREDIT")
      .reduce((sum, t) => sum + t.amountCents, 0) / 100;

  const previousDebits =
    previousPeriod
      .filter((t) => t.type === "DEBIT")
      .reduce((sum, t) => sum + t.amountCents, 0) / 100;

  const creditChange =
    previousCredits > 0 ? ((currentCredits - previousCredits) / previousCredits) * 100 : 0;
  const debitChange =
    previousDebits > 0 ? ((currentDebits - previousDebits) / previousDebits) * 100 : 0;

  const currentNet = currentCredits - currentDebits;
  const previousNet = previousCredits - previousDebits;
  const netChange = previousNet !== 0 ? ((currentNet - previousNet) / Math.abs(previousNet)) * 100 : 0;

  return {
    credits: {
      value: Math.round(currentCredits),
      change: Math.round(creditChange),
      trend: creditChange > 0 ? "up" : creditChange < 0 ? "down" : "neutral",
    },
    debits: {
      value: Math.round(currentDebits),
      change: Math.round(debitChange),
      trend: debitChange > 0 ? "up" : debitChange < 0 ? "down" : "neutral",
    },
    net: {
      value: Math.round(currentNet),
      change: Math.round(netChange),
      trend: netChange > 0 ? "up" : netChange < 0 ? "down" : "neutral",
    },
  };
}

// Format currency for charts (deprecated: use useOrgSettings().formatCurrency instead)
export function formatINR(amount: number): string {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

// Prepare daily spending data for bar charts
export function prepareDailySpending(transactions: Transaction[], days: number = 7): SpendingDataPoint[] {
  return prepareSpendingData(transactions, days);
}

// Group transactions by card for spending distribution
export function prepareCardSpending(
  transactions: Transaction[],
  cardMap: Map<string, string>
): { cardName: string; amount: number; percentage: number; color: string }[] {
  const cardSpending = new Map<string, number>();

  transactions
    .filter((t) => t.type === "DEBIT")
    .forEach((txn) => {
      // This would need cardId from transaction data
      // For now, we'll use a placeholder
      const cardId = "default";
      const cardName = cardMap.get(cardId) || "Card";
      cardSpending.set(cardName, (cardSpending.get(cardName) || 0) + txn.amountCents / 100);
    });

  const total = Array.from(cardSpending.values()).reduce((sum, val) => sum + val, 0);

  const colors = [
    "hsl(var(--chart-1))",
    "hsl(var(--chart-2))",
    "hsl(var(--chart-3))",
    "hsl(var(--chart-4))",
    "hsl(var(--chart-5))",
  ];

  return Array.from(cardSpending.entries())
    .map(([cardName, amount], index) => ({
      cardName,
      amount: Math.round(amount),
      percentage: total > 0 ? Math.round((amount / total) * 100) : 0,
      color: colors[index % colors.length],
    }))
    .sort((a, b) => b.amount - a.amount);
}
